import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, apiErr, CONF, STATE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReviewPanel from "@/components/ReviewPanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Zap, ShieldAlert, Inbox, Lock, LockOpen, Timer, AlertTriangle } from "lucide-react";

function lockInfo(lockedAt, timeout) {
  if (!lockedAt) return { expired: true, mins: 0 };
  const elapsed = (Date.now() - new Date(lockedAt).getTime()) / 60000;
  return { expired: elapsed >= timeout, mins: Math.max(0, Math.round(timeout - elapsed)) };
}

function fmtDur(s) {
  if (s == null) return "-";
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

export default function ApprovalQueue({ projectId: fixedPid = null, embedded = false, bidangOptions = null }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState(fixedPid || "");
  const [pending, setPending] = useState([]);
  const [locked, setLocked] = useState([]);
  const [current, setCurrent] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [lockTimeout, setLockTimeout] = useState(30);
  const [, setTick] = useState(0);
  const PAGE_SIZE = 50;
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [lockedPage, setLockedPage] = useState(1);
  const [lockedTotal, setLockedTotal] = useState(0);
  const [loadingMorePending, setLoadingMorePending] = useState(false);
  const [loadingMoreLocked, setLoadingMoreLocked] = useState(false);
  const pendingScrollRef = useRef(null);
  const lockedScrollRef = useRef(null);

  useEffect(() => {
    if (fixedPid) { setPid(fixedPid); return; }
    api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setPid(r.data[0].id); }).catch(() => {});
  }, [fixedPid]);

  const loadPending = useCallback(() => {
    if (!pid) return;
    setPendingPage(1); setLockedPage(1);
    api.get(`/projects/${pid}/records?status=pending_review&page=1&page_size=${PAGE_SIZE}`).then((r) => {
      setPending(r.data.items); setPendingTotal(r.data.total);
      if (r.data.lock_timeout_minutes) setLockTimeout(r.data.lock_timeout_minutes);
    }).catch(() => {});
    api.get(`/projects/${pid}/records?status=in_review&page=1&page_size=${PAGE_SIZE}`).then((r) => {
      setLocked(r.data.items); setLockedTotal(r.data.total);
    }).catch(() => {});
  }, [pid]);
  useEffect(() => { setCurrent(null); loadPending(); }, [loadPending]);
  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 30000); return () => clearInterval(t); }, []);

  const loadMorePending = useCallback(() => {
    if (!pid || loadingMorePending) return;
    if (pending.length >= pendingTotal) return;
    setLoadingMorePending(true);
    const nextPage = pendingPage + 1;
    api.get(`/projects/${pid}/records?status=pending_review&page=${nextPage}&page_size=${PAGE_SIZE}`)
      .then((r) => { setPending((prev) => [...prev, ...r.data.items]); setPendingPage(nextPage); })
      .finally(() => setLoadingMorePending(false));
  }, [pid, pending.length, pendingTotal, pendingPage, loadingMorePending]);

  const loadMoreLocked = useCallback(() => {
    if (!pid || loadingMoreLocked) return;
    if (locked.length >= lockedTotal) return;
    setLoadingMoreLocked(true);
    const nextPage = lockedPage + 1;
    api.get(`/projects/${pid}/records?status=in_review&page=${nextPage}&page_size=${PAGE_SIZE}`)
      .then((r) => { setLocked((prev) => [...prev, ...r.data.items]); setLockedPage(nextPage); })
      .finally(() => setLoadingMoreLocked(false));
  }, [pid, locked.length, lockedTotal, lockedPage, loadingMoreLocked]);

  useEffect(() => {
    const el = pendingScrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) loadMorePending();
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [loadMorePending, current]);

  useEffect(() => {
    const el = lockedScrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) loadMoreLocked();
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [loadMoreLocked, current]);

  const forceRelease = async (rid) => {
    try {
      await api.post(`/projects/${pid}/records/${rid}/force-release`);
      toast.success("Lock dilepas paksa oleh admin"); loadPending();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const claimNext = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post(`/projects/${pid}/approval/claim-next`);
      if (data.record) { setCurrent(data.record); if (data.lock_timeout_minutes) setLockTimeout(data.lock_timeout_minutes); toast.success("Record berikutnya diklaim"); }
      else toast.info(data.message || "Tidak ada record tersedia");
      loadPending();
    } catch (e) { toast.error(apiErr(e)); } finally { setClaiming(false); }
  };

  const openRecord = async (rid) => {
    try { const { data } = await api.post(`/projects/${pid}/records/${rid}/claim`); setCurrent(data); loadPending(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          {!embedded && <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">Antrian Persetujuan</p>}
          <h1 className={`font-heading font-bold tracking-tight ${embedded ? "text-xl" : "text-3xl"}`}>Approval Queue</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5" /> Satu record = satu reviewer aktif. Concurrency-safe.</p>
        </div>
        <div className="flex items-center gap-2">
          {!embedded && (
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger className="w-60" data-testid="aq-project-select"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button onClick={claimNext} disabled={!pid || claiming} className="rounded-full" data-testid="claim-next-btn">
            <Zap className="h-4 w-4 mr-1.5" /> Claim Next Record
          </Button>
        </div>
      </div>

      {current ? (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold">Sedang Direview</h3>
            <Button variant="ghost" size="sm" onClick={() => { setCurrent(null); loadPending(); }} data-testid="aq-close">Tutup</Button>
          </div>
          <ReviewPanel key={current?.id} projectId={pid} record={current} lockTimeout={lockTimeout} bidangOptions={bidangOptions ?? ((projects.find((p) => p.id === pid)?.bidang_list) || [])} onChanged={() => { loadPending(); }} />
        </Card>
      ) : (
        <>
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2"><Inbox className="h-4 w-4 text-primary" /><span className="font-semibold text-sm">Menunggu Review ({pendingTotal})</span></div>
          {pending.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">Tidak ada record menunggu review.</div>
          ) : (
            <div ref={pendingScrollRef} className="divide-y divide-border max-h-[600px] overflow-y-auto">
              {pending.map((r, i) => (
                <motion.button key={r.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 20) * 0.02 }}
                  onClick={() => openRecord(r.id)} data-testid={`pending-${r.id}`}
                  className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{r.data?.uraian_informasi || "(tanpa uraian)"}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {(r.sub_project || r.data?.bidang) && (
                        <span className="inline-block px-1.5 py-0.2 mr-1.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-600 border border-blue-200">
                          {r.sub_project || r.data?.bidang}
                        </span>
                      )}
                      No. Arsip: {r.data.nomor_berkas || "-"} · Klasifikasi: {r.data.kode_klasifikasi || "-"} · Diunggah oleh: {r.uploaded_by_name || "-"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={`border text-[10px] ${STATE[r.state]?.cls || STATE.WAITING.cls}`}>{STATE[r.state]?.label || "Menunggu"}</Badge>
                    {r.possible_duplicate && <Badge variant="outline" className="text-red-600 border-red-300 text-[10px]" data-testid={`dup-badge-${r.id}`}>Kemungkinan Duplikat</Badge>}
                    {r.needs_human_review && <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">Perlu Review</Badge>}
                    <Badge variant="outline" className={`border ${CONF[r.confidence_level] || CONF.LOW}`}>{r.confidence_level} {r.confidence}%</Badge>
                  </div>
                </motion.button>
              ))}
              {loadingMorePending && (
                <p className="text-center text-xs text-muted-foreground py-3">Memuat lebih banyak...</p>
              )}
            </div>
          )}
        </Card>

        {lockedTotal > 0 && (
          <Card className="overflow-hidden" data-testid="locked-records-card">
            <div className="p-4 border-b border-border flex items-center gap-2">
              <Lock className="h-4 w-4 text-amber-600" />
              <span className="font-semibold text-sm">Sedang Dikunci ({lockedTotal})</span>
              <span className="text-xs text-muted-foreground ml-1">Timeout otomatis {lockTimeout} menit</span>
            </div>
            <div ref={lockedScrollRef} className="divide-y divide-border max-h-[400px] overflow-y-auto">
              {locked.map((r) => {
                const info = lockInfo(r.locked_at, lockTimeout);
                const mine = r.locked_by === user?.id;
                return (
                  <div key={r.id} data-testid={`locked-${r.id}`}
                    className="px-4 py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{r.data?.uraian_informasi || "(tanpa uraian)"}</p>
                      <div className="text-xs text-muted-foreground flex items-center flex-wrap gap-1.5 mt-0.5">
                        <Lock className="h-3 w-3" /> Direview oleh {mine ? "Anda" : (r.locked_by_name || "pengguna")}
                        <span>· Durasi {fmtDur(r.review_seconds)}</span>
                        {info.expired ? (
                          <Badge variant="outline" className="text-red-600 border-red-300 text-[10px] ml-1"><AlertTriangle className="h-3 w-3 mr-1" />Lock kedaluwarsa</Badge>
                        ) : (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-[10px] ml-1"><Timer className="h-3 w-3 mr-1" />~{info.mins} mnt tersisa</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {mine && (
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => openRecord(r.id)} data-testid={`resume-${r.id}`}>Lanjutkan</Button>
                      )}
                      {info.expired && !mine && (
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => openRecord(r.id)} data-testid={`claim-expired-${r.id}`}>Ambil Alih</Button>
                      )}
                      {user?.role === "SUPER_ADMIN" && (
                        <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200"
                          onClick={() => forceRelease(r.id)} data-testid={`force-release-${r.id}`}>
                          <LockOpen className="h-4 w-4 mr-1.5" /> Lepas Paksa
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {loadingMoreLocked && (
                <p className="text-center text-xs text-muted-foreground py-3">Memuat lebih banyak...</p>
              )}
            </div>
          </Card>
        )}
        </>
      )}
    </div>
  );
}
